import { Box, Button, Flex, HStack, SimpleGrid, VStack } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import { SFRPropertyStatus } from '@services/appsync/types';
import React from 'react';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { Helmet } from 'react-helmet'
import { UI } from 'src/apps/invest/common/shells/UI';
import { PROPERTY_STATUS, PROPERTY_TAG, ROUTES } from 'src/apps/invest/config';
import { PropertyItemTransition } from './PropertyItemTransition';

export const PropertiesList: React.FC = () => {
  const model = usePropertiesModel();

  const dollarFormat = (dollar: number) => currency.format(dollar, { maximumFractionDigits: 0 });

  return (
    <UI containerPadding={model.properties.length === 0 ? 0 : undefined}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Properties in Home Shares | Concreit</title>
      </Helmet>
      {model.properties.length > 0 ? (
        <SimpleGrid columns={[1, 1, 2, 3]} gap="27px">
          {model.properties.map((property, i) => {
            const propertyStatus: SFRPropertyStatus = property?.sfr?.status.status || SFRPropertyStatus.NEW;
            const totalPropertyAmount = Number(property.target_amount);
            const currentAmountRaised = Number(property.raised_amount?.total);
            const fundedPercent = ((currentAmountRaised / totalPropertyAmount) * 100).toFixed(0);
            const soldOut = property.sfr?.status.status === SFRPropertyStatus.SOLD_OUT;

            return (
              Boolean(property.slug) && (
                <PropertyItemTransition key={`prop-${i}`} delay={0.1 * i}>
                  <VStack
                    as={Link}
                    align="start"
                    to={ROUTES.propertyDetail(property.slug as string)}
                    style={{
                      cursor: 'pointer',
                    }}>
                    {/* Property Image */}
                    <Box
                      backgroundImage={
                        property?.sfr?.info.images && property.sfr.info.images.length > 0
                          ? property.sfr.info.images[0].url
                          : undefined
                      }
                      backgroundRepeat="no-repeat"
                      backgroundSize="cover"
                      backgroundPosition={'center center'}
                      borderRadius={'10px'}
                      position="relative"
                      opacity={soldOut ? 0.6 : 1}
                      w="100%"
                      h={'280px'}>
                      {PROPERTY_STATUS[propertyStatus] && (
                        <Box
                          alignItems={'center'}
                          justifyContent="center"
                          display="flex"
                          bgColor={PROPERTY_STATUS[propertyStatus][3] ?? 'green.500'}
                          color={PROPERTY_STATUS[propertyStatus][4] ?? 'white'}
                          borderRadius={'6px'}
                          fontSize="12px"
                          textTransform={'uppercase'}
                          fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                          px="8px"
                          py="2px"
                          position="absolute"
                          top="15px"
                          right="15px">
                          <Icon
                            icon={PROPERTY_STATUS[propertyStatus][1] ?? 'bolt'}
                            mr="8px"
                            variant={PROPERTY_STATUS[propertyStatus][2] ?? 'solid'}
                          />
                          {PROPERTY_STATUS[propertyStatus][0] ?? 'Featured'}
                        </Box>
                      )}
                    </Box>
                    {/* Property Name */}
                    <HStack w="full" justify={'space-between'}>
                      <TextBlock weight="bold" lineHeight={'24px'} fontSize="24px">
                        {property.display_name}
                      </TextBlock>
                      {/*
                 Like Button:
                 XXX/TODO: Need a backend for this
                 */}
                      {/*<LikeButton />*/}
                    </HStack>
                    {/* Funding */}
                    <Flex alignItems="center" gap={'5px'}>
                      <Icon
                        borderWidth={'1px'}
                        borderColor="gray.500"
                        borderRadius={'10px'}
                        padding="2px"
                        icon="dollar-sign"
                        color="gray.500"
                        boxSize={'12px'}
                        variant="regular"
                        mr="2px"
                      />
                      {soldOut ? (
                        <>
                          <Box color="gray.500" fontSize="12px">
                            $0 Available
                          </Box>
                          <Box color="black" fontSize="12px" mx={'5px'}>
                            |
                          </Box>
                          <Icon icon="user" color="gray.500" boxSize={'12px'} variant="regular" />
                          <Box color="gray.500" fontSize="12px" ml={0}>
                            100% Fully Funded
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box color="gray.500" fontSize="12px">
                            {dollarFormat(totalPropertyAmount - currentAmountRaised)} Available
                          </Box>
                          <Box color="black" fontSize="12px" mx={'5px'}>
                            |
                          </Box>
                          <Icon icon="user" color="gray.500" boxSize={'12px'} variant="regular" />
                          <Box color="gray.500" fontSize="12px" ml={0}>
                            {`${fundedPercent}% Funded`}
                          </Box>
                        </>
                      )}
                    </Flex>
                    {/* Tags */}
                    <HStack>
                      {property?.sfr?.info?.tags?.map((tag, i) => (
                        <Box
                          display={'flex'}
                          alignItems={'center'}
                          justifyContent="center"
                          key={`tag-${i}`}
                          color="gray.500"
                          fontSize="12px"
                          bgColor={'gray.50'}
                          px="7px"
                          borderRadius={'7px'}
                          h="25px">
                          <Icon icon={PROPERTY_TAG[tag][1]} mr="5px" variant={PROPERTY_TAG[tag][2]} />{' '}
                          {PROPERTY_TAG[tag][0]}
                        </Box>
                      ))}
                    </HStack>
                    {/* 
                Investors 
                XXX/TODO: Need a backend for this
              */}
                    {/*
                    <HStack py="5px">
                      <AvatarGroup size="sm" max={2}>
                        <Avatar
                          name="Sean Hsieh"
                          src="https://assets.website-files.com/620d28fd9739978f6d377f52/6265e7b57e2254daa692d5e8_2020%20Sean%20headshot.JPG"
                        />
                        <Avatar
                          name="Jordan Levy"
                          src="https://assets.website-files.com/620d28fd9739978f6d377f52/62267ccd4bdc59472d818d62_2022%20Jordan%20headshot.png"
                        />
                        <Avatar
                          name="Chris Garnett"
                          src="https://assets.website-files.com/620d28fd9739978f6d377f52/6261eaf9ff15b23c89553668_DSC03596.JPG"
                        />
                        <Avatar
                          name="Sean Hsieh"
                          src="https://assets.website-files.com/620d28fd9739978f6d377f52/6265e7b57e2254daa692d5e8_2020%20Sean%20headshot.JPG"
                        />
                        <Avatar
                          name="Jordan Levy"
                          src="https://assets.website-files.com/620d28fd9739978f6d377f52/62267ccd4bdc59472d818d62_2022%20Jordan%20headshot.png"
                        />
                        <Avatar
                          name="Chris Garnett"
                          src="https://assets.website-files.com/620d28fd9739978f6d377f52/6261eaf9ff15b23c89553668_DSC03596.JPG"
                        />
                      </AvatarGroup>
                      <Box
                        cursor={'pointer'}
                        alignItems={'center'}
                        justifyContent="center"
                        backgroundColor="blue.50"
                        color="blue.500"
                        borderRadius={'5px'}
                        px="10px"
                        fontSize="12px"
                        lineHeight={'25px'}
                        fontWeight={FontWeight.HELVETICA_NOW_BOLD}
                        _hover={{
                          backgroundColor: 'blue.100',
                        }}>
                        20 investors <Icon icon="chevron-right" color="blue.500" boxSize={'8px'} ml="5px" />
                      </Box>
                    </HStack>
                   */}
                  </VStack>
                </PropertyItemTransition>
              )
            );
          })}
        </SimpleGrid>
      ) : (
        <Flex mt={'60px'} padding={['20px', '0']} flex={1} justifyContent="center">
          <Flex
            p={['10px', '10px', '0px']}
            maxWidth={['100%', '100%', '1000px', '1440px']}
            w={'100%'}
            backgroundSize={['cover', 'cover', 'cover', 'cover', '100%']}
            backgroundRepeat={'no-repeat'}
            flex={1}
            backgroundPosition={['center', 'center', 'bottom']}
            backgroundImage={[
              '/assets/gfx/blurred-properties-mobile.png',
              '/assets/gfx/blurred-properties-mobile.png',
              '/assets/gfx/blurred-properties.png',
            ]}>
            <Flex flexDirection={'column'} w={'100%'} maxW={'450px'} m={' 0 auto'} alignItems="center">
              <TextBlock textAlign="center" lineHeight="40px" weight="bold" fontSize="36px">
                No available properties at the moment
              </TextBlock>
              <TextBlock maxW={'350px'} textAlign="center">
                Please check back soon as we are working hard to make more opportunities available to you.
              </TextBlock>
              <Button
                as={Link}
                to={ROUTES.dashboard}
                mt={'24px'}
                maxW={'350px'}
                w={'100%'}
                rightIcon={<Icon icon="arrow-right-long" />}>
                Return to Dashboard
              </Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </UI>
  );
};
